import type { Ref } from 'vue';
import type { Page } from '@zotlo-shared/types/model';
import { applyTemplateVariables } from '@zotlo-shared/utils/applyTemplateVariables';
import { useSiteDataOnClient } from '~/composables/useSiteDataOnClient';
import { COOKIE, getClientCookie } from '#shared/utils/cookie';
import { waitForIntegrations } from '@/utils';
import { reloadSessionId } from '@/utils/session';
import { GTM, GA4, Facebook, Tiktok } from '@/utils/integrations';
import { Payment } from '@/services/payment';
import { getBaseUrl, addQueryParamsToUrl } from '#shared/utils/getBaseUrl';
import { useDocLinks } from './useDocLinks';
import { getPackagePrices, getPackagePeriodsInfo } from '@zotlo-shared/elements/common/payment/utils';
import { PackagePaymentType, TrialPackageType } from '@zotlo-shared/elements/common/payment/types';
import { globalEmit } from '@zotlo-shared/utils/helpers';
import { UserActivationType } from '@zotlo-shared/types';

export function usePaymentSuccess(page: Ref<Page>) {
  const route = useRoute();
  const { t: $t } = useI18n();
  const { slug } = getBaseUrl(route?.fullPath);
  const cookiePath = `/${slug}`;
  const subscriberId = useCookie(COOKIE.SUBSCRIBER_ID, { sameSite: 'none', secure: true, path: cookiePath });
  const isUserAlreadySubscribed = useCookie(COOKIE.USER_ALREADY_SUBSCRIBED, { sameSite: 'none', secure: true, path: cookiePath });
  const countdownStartedAt = useCookie(COOKIE.COUNTDOWN_STARTED_AT, { sameSite: 'none', secure: true, path: cookiePath });
  const { isPreview, isSandbox, applicationLinks, siteData, packages } = useSiteDataOnClient();

  const subscriptionDetail = ref();
  const pageData = ref({
    applicationLinks: applicationLinks.value,
    deeplinkSettings: siteData.value?.deeplinkSettings,
    hiddenConditionalRefs: getHiddenConditionalRefs(),
    isSuccessMode: !isUserAlreadySubscribed.value,
    countryCode: siteData.value?.countryCode,
    docLinks: useDocLinks(),
    paymentDetails: {},
    isEpinViewActive: getIsEpinViewActive(),
    userActivationType: getUserActivationType(),
    activationCode: getUserActivationCode(),
    epinCode: getEpinCodeValue(),
    zcSource: useRuntimeConfig().public.NUXT_ZOTLO_STORE_URL + 'account',
  });

  function paymentGTMSuccess() {
    GTM.push({
      event: 'success',
      successType: 'Payment'
    });

    GA4.gtag('event', 'success', {
      successType: 'Payment'
    });
  }

  function paymentSuccessGTMpageView() {
    GA4.gtag('event', 'page_view', {
      page_title: document?.title || '',
      page_location: window.location.pathname || '',
      previous_page_path: new URL(document.referrer || window.location.origin)?.pathname || '',
    });
  }

  function paymentIntegrationCompleted() {
    const app = subscriptionDetail.value?.application || {};
    const packageData = subscriptionDetail.value?.package || {};
    const utmSource = JSON.parse(getClientCookie(COOKIE.UTM) || '{}')?.utm_source || '';

    const {
      transaction_id: transactionId = ' ',
      currency = ' ',
      price = '0.00',
      provider_name: paymentMethod = ''
    } = subscriptionDetail.value?.transaction?.[0] || {};

    const priceValue = parseFloat(price || "0");

    paymentGTMSuccess();

    const gtmObj = {
      transaction_id: transactionId,
      value: priceValue,
      currency,
      coupon: ' ',
      subscriber_id: subscriberId.value || '',
      items: [
        {
          item_id: packageData.packageId,
          item_name: packageData.name,
          affiliation: utmSource,
          coupon: ' ',
          index: 1,
          item_brand: app.name || ' ',
          item_category: packageData.periodType || ' ',
          price: priceValue,
          quantity: 1
        }
      ]
    }

    const subscriptionStarted = {
      subscriber_id: gtmObj.subscriber_id,
      transaction_id: gtmObj.transaction_id
    }

    GTM.push({ event: 'subscription_started', ...subscriptionStarted });
    GA4.gtag('event', 'subscription_started', subscriptionStarted);
    GTM.push({ event: 'purchase', payment_method: paymentMethod, ecommerce: gtmObj });
    GA4.gtag('event', 'purchase', { payment_method: paymentMethod, ...gtmObj, });

    if (GA4.options.googleAds.isActive) {
      GA4.gtag('event', 'conversion', {
        send_to: GA4.getConversionLabel(),
        value: gtmObj.value,
        currency: gtmObj.currency,
        transaction_id: gtmObj.transaction_id
      })
    }

    const { user_data: fbSiteData, context: tiktokContext } = getUserDataForIntegration({
      registerType: siteData.value?.registerType || '',
      subscriberId: subscriberId.value || '',
    });

    const contents = [{
      content_id: packageData.packageId,
      content_name: packageData.name,
      price: priceValue,
      quantity: 1
    }]

    Facebook.purchase({
      value: priceValue,
      currency,
      userData: fbSiteData,
      otherData: {
        order_id: transactionId,
        packageId: packageData.packageId,
      }
    });

    Tiktok.identify(tiktokContext);
    Tiktok.purchase({
      value: priceValue,
      currency,
      description: app.name || ' ',
      content_id: packageData.packageId,
      orderID: transactionId,
      context: tiktokContext,
      contents
    });

    if (packageData.packageType === PackagePaymentType.SUBSCRIPTION) {
      const eventName = packageData.trialPackageType === TrialPackageType.NO ? 'Subscribe' : 'StartTrial';
      const fbObj = {
        value: priceValue,
        currency,
        packageId: packageData.packageId,
        ...fbSiteData
      };

      const ttObj = {
        value: priceValue,
        currency,
        content_type: 'product',
        contents: [{
          content_id: packageData?.packageId,
          content_name: packageData?.name,
          price: priceValue,
          quantity: 1
        }],
        quantity: 1,
        ...tiktokContext
      }

      Facebook.track(eventName, fbObj);
      Tiktok.track(eventName, ttObj);
    }
  }

  function paymentSuccessGTMClick(event: any) {
    GTM.push({
      event: 'customClick',
      clickCategory: 'Thank You Page',
      clickName: event.target.innerText
    });

    GA4.gtag('event', 'customClick', {
      clickCategory: 'Thank You Page',
      clickName: event.target.innerText
    });
  }

  function loadGTMClickEvents() {
    if (isPreview.value) return;
    const buttons = document.querySelectorAll('.tw-element-button, a[href]:not([href="#"]):not([href=""])');

    for (const button of buttons) {
      button.addEventListener('click', paymentSuccessGTMClick);
    }
  }

  function clearSubscriberRelatedCookies() {
    subscriberId.value = '';
    isUserAlreadySubscribed.value = ''
    countdownStartedAt.value = '';
  }

  function getHiddenConditionalRefs() {
    const registerTypeRefs = siteData.value?.registerType === 'phoneNumber' ? ['mailSpecificElement']: ['phoneSpecificElement'];
    const userAlreadySubscribedRefs = isUserAlreadySubscribed.value ? ['successElement'] : ['errorElement'];
    return [...registerTypeRefs, ...userAlreadySubscribedRefs];
  }

  function getIsEpinViewActive() {
    const isFirstPackageEpin = packages.value?.[0]?.packageType === PackagePaymentType.EPIN;
    if (isPreview.value && !isSandbox.value) return isFirstPackageEpin;
    const hasEpinCode = !!siteData?.value?.epinDetails?.epinCode;
    return hasEpinCode;
  }

  function getUserActivationType() {
    return siteData?.value?.userActivationData?.userActivation || UserActivationType.EMAIL_AND_PHONE;
  }

  function getUserActivationCode() {
    if (isPreview.value && !isSandbox.value) return '';
    return siteData?.value?.sessionId || '';
  }

  function getEpinCodeValue() {
    return siteData?.value?.epinDetails?.epinCode;
  }

  function getPaymentAmountText(packageItem: Record<string, any>) {
    const { trialPrice, price } = getPackagePrices(packageItem);
    const { period:periodCount, periodType, trialPeriod:trialPeriodCount, trialPeriodType } = getPackagePeriodsInfo(packageItem);
    const period = $t(`generate.periods.${periodType}`, {
      count: periodCount,
    });
    const trialPeriod = $t(`generate.periods.${trialPeriodType}`, {
      count: trialPeriodCount,
    });
    const localizationVars = {
      price,
      period,
      trialPrice,
      trialPeriod,
    };
    switch(packageItem?.packageType) {
      case PackagePaymentType.CONSUMABLE:
        return $t("generate.paymentSuccess.paymentDetails.packagePriceInfo.consumable", localizationVars);
      case PackagePaymentType.EPIN:
        return $t("generate.paymentSuccess.paymentDetails.packagePriceInfo.epin", localizationVars);
      case PackagePaymentType.SUBSCRIPTION:
        switch (packageItem?.trialPackageType) {
          case TrialPackageType.FREE_TRIAL:
            return $t("generate.paymentSuccess.paymentDetails.packagePriceInfo.freeTrial", localizationVars);
          case TrialPackageType.STARTING_PRICE:
            return $t("generate.paymentSuccess.paymentDetails.packagePriceInfo.startingPrice", localizationVars);
          case TrialPackageType.NO:
            return $t("generate.paymentSuccess.paymentDetails.packagePriceInfo.noTrial", localizationVars);
          default:
            return "";
        }
      default:
        return "";
    }
  }

  function setPaymentSuccessDetails(subscriptionDetailResult: Record<string, any>) {
    if (Array.isArray(subscriptionDetailResult)) return;
    const { application, package:packageItem, transaction, client } = subscriptionDetailResult || {};
    const { purchase_date = '', expire_date = '', provider_key_translation:paymentMethod = '', provider_key:providerKey = '' } = transaction?.[0] || {};
    subscriberId.value = client?.subscriberId || '';
    const paymentAmount = getPaymentAmountText(packageItem);
    const details = {
      product: application?.name || '',
      paidOn: purchase_date,
      expiresOn: expire_date,
      paymentMethod,
      providerKey,
      paymentAmount,
      packageType: packageItem?.packageType,
      trialPackageType: packageItem?.trialPackageType || '',
      customerId: subscriberId.value,
    }
    pageData.value.paymentDetails = details;
    handleTemplateVariables();
  }

  globalEmit('pinCodeCopied', function () {
    clearToasts();
    showToast($t('generate.common.codeCopied'), '', 'success', { timeout: 3000 });
  });

  async function initEventsAndIntegrations() {
    loadGTMClickEvents();
    return Payment.getSubscriptionDetail().then(async (response) => {
      if (response.data.meta?.errorCode) return;
      subscriptionDetail.value = response.data.result;
      if (response.data.result?.zcSource) {
        pageData.value.zcSource += '?zc_source='+ response.data.result?.zcSource || '';
      }

      setPaymentSuccessDetails(response.data.result);
      await waitForIntegrations();
      return paymentIntegrationCompleted();
    });
  }

  function handleWebsiteRedirectionsSettings() {
    const { status = 0, successPageUrl = '', warningPageUrl = '' } = siteData?.value?.websiteRedirectionSettings || {};
    if (status && globalThis?.location?.href) {
      const transactionId = (isPreview.value && !isSandbox.value) ? '' : subscriptionDetail?.value?.transaction?.[0]?.transaction_id || '';
      const clientUuid = (isPreview.value && !isSandbox.value) ? '' : siteData?.value?.sessionId || '';
      const successURL = addQueryParamsToUrl(successPageUrl, { transactionId, clientUuid });
      const warningURL = addQueryParamsToUrl(warningPageUrl, { clientUuid });
      globalThis.location.href = pageData.value.isSuccessMode ? successURL : warningURL;
    }
  }

  function applyDeeplinkVariables() {
    const appLinksTags = document.querySelectorAll("a");
    const userId = subscriberId.value || '';
    const transactionId = subscriptionDetail?.value?.transaction?.[0]?.transaction_id || '';
    appLinksTags?.forEach((link) => {
      const href = link?.getAttribute('href') || '';
      const updatedHref = href
        .replace(new RegExp('{user_id}', 'g'), userId)
        .replace(new RegExp('{transaction_id}', 'g'), transactionId);
      link.setAttribute('href', updatedHref);
    });
  }

  function handleTemplateVariables() {
    applyTemplateVariables({
      USER_MAIL: subscriberId.value || '',
      PHONE_NUMBER: subscriberId.value ? `+${subscriberId.value}` : '',
      PACKAGE_INSTRUCTIONS: siteData?.value?.epinDetails?.usage?.[0]?.content || '',
      ACTIVATION_INSTRUCTIONS: siteData?.value?.userActivationData?.instruction || '',
    });
    applyDeeplinkVariables();
  }

  function init() {
    setTimeout(() => {
      nextTick(async () => {
        if (!(isPreview.value && !isSandbox.value)) {
          if (isUserAlreadySubscribed.value) {
            handleTemplateVariables();
          } else {
            await initEventsAndIntegrations();
          }
          reloadSessionId((isPreview.value && !isSandbox.value), cookiePath);
        }
        clearSubscriberRelatedCookies();
        paymentSuccessGTMpageView();
        handleWebsiteRedirectionsSettings();
      });
    }, 100);
  }

  onMounted(() => {
    init();
  });

  return { page, pageData };
}
